import React from "react";
import { Button, ProgressBar, Form, Alert } from "react-bootstrap";
import { MdDelete, MdEdit, MdPersonAdd } from "react-icons/md";
import {
  borrowerShareholders,
  shareholdersOf,
  totalHolding,
  MIN_OWNER_DECLARATION,
  MAX_OWNER_DECLARATION,
} from "./shareholding_rules";
import { useExpandedCompanies } from "./use_expanded_companies";
import "./shareholders.css";

const formatPct = (value) => `${Number(value || 0).toFixed(2)}%`;

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("") || "?";

const Pill = ({ tone, children }) => <span className={`sh-pill sh-pill-${tone}`}>{children}</span>;

const TypePill = ({ type }) =>
  type === "BUSINESS" ? <Pill tone="business">Business</Pill> : <Pill tone="individual">Individual</Pill>;

const BeneficiaryPill = ({ isOwner }) =>
  isOwner ? <Pill tone="yes">Yes</Pill> : <Pill tone="no">No</Pill>;

// Where a company's own owners stand against the 50%-100% rule, for the
// card header badge. Incompleteness is the thing this view leads with, not
// a footnote under the name.
const ownerDeclarationStatus = (ownersTotal) => {
  if (ownersTotal > MAX_OWNER_DECLARATION) {
    return { tone: "danger", label: `${formatPct(ownersTotal)} declared — exceeds 100%` };
  }
  if (ownersTotal >= MIN_OWNER_DECLARATION) {
    return { tone: "success", label: `${formatPct(ownersTotal)} declared` };
  }
  return { tone: "warning", label: `${formatPct(ownersTotal)} of ${MIN_OWNER_DECLARATION}% required` };
};

const overallVariant = (total) => {
  if (total > MAX_OWNER_DECLARATION) return "danger";
  if (total >= MIN_OWNER_DECLARATION) return "success";
  return "warning";
};

const ShareholdersTable = ({ shareholders = [], coverageErrors = [], onEdit, onRemove, onAdd, goToNextStep, isCreditOfferApproved = false }) => {
  const borrowerRows = borrowerShareholders(shareholders);
  const totalShareholding = totalHolding(borrowerRows);

  // A plain row never has its own owners to declare; a company row above the
  // UBO threshold does, and leads the view as its own card instead.
  const plainRows = borrowerRows.filter((item) => !item.requires_own_shareholders);
  const companyRows = borrowerRows.filter((item) => item.requires_own_shareholders);

  const { isExpanded, toggleCompany } = useExpandedCompanies();

  // Pre-tick the acknowledgement when the declared shareholding already adds up,
  // and re-sync it whenever an edit changes whether coverage is still satisfied.
  const [confirmed, setConfirmed] = React.useState(() => coverageErrors.length === 0);
  React.useEffect(() => {
    if (coverageErrors.length === 0) setConfirmed(true);
  }, [coverageErrors.length]);

  const canProceed = confirmed && coverageErrors.length === 0;

  // A verified shareholder is settled; the backend rejects any change to it.
  const rowActions = (item, company) => {
    const locked = isCreditOfferApproved || item.is_kyc_verified;
    const lockReason = item.is_kyc_verified
      ? "This shareholder's KYC has been verified and can no longer be changed"
      : undefined;
    return (
      <div
        className="sh-actions"
        // Action buttons sit inside a clickable card header / row; a real
        // <button> is safe to nest in a <div>, but the click must not also
        // toggle the row open/closed.
        onClick={(event) => event.stopPropagation()}
      >
        {company && (
          <Button
            variant="outline-primary"
            title={`Add an individual shareholder of ${company.name}`}
            onClick={() => onAdd(company)}
            disabled={isCreditOfferApproved}
          >
            <MdPersonAdd />
          </Button>
        )}
        <Button
          variant="outline-primary"
          title={lockReason || "Edit"}
          onClick={() => onEdit(item)}
          disabled={locked}
        >
          <MdEdit />
        </Button>
        <Button
          variant="outline-danger"
          title={lockReason || "Remove"}
          onClick={() => onRemove(item)}
          disabled={locked}
        >
          <MdDelete />
        </Button>
      </div>
    );
  };

  const nameCell = (name, isBusiness) => (
    <div className="sh-name-cell">
      <span className={`sh-avatar ${isBusiness ? "sh-avatar-business" : ""}`}>{initials(name)}</span>
      <span className="text-capitalize">{name}</span>
    </div>
  );

  return (
    <div className="sh-wrap">
      {/* 1. Add New Shareholder */}
      {!isCreditOfferApproved && (
        <div className="d-flex align-items-center justify-content-end">
          <Button variant="outline-primary" onClick={() => onAdd(null)}>
            + Add New Shareholder
          </Button>
        </div>
      )}

      {borrowerRows.length === 0 ? (
        <p className="text-muted mb-0">No shareholders added yet.</p>
      ) : (
        <>
          {/* 2. Business shareholders that must declare their own owners, front
              and center as cards — whether they're done is the headline, not
              a line of muted text you'd have to notice. */}
          {companyRows.map((item) => {
            const owners = shareholdersOf(shareholders, item);
            const ownersTotal = totalHolding(owners);
            const status = ownerDeclarationStatus(ownersTotal);
            const expanded = isExpanded(item.uuid);
            return (
              <div className="sh-card" key={item.uuid}>
                <div
                  className="sh-card-header d-flex align-items-center flex-wrap gap-2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={() => toggleCompany(item.uuid)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleCompany(item.uuid);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <span className="sh-chevron">{expanded ? "▾" : "▸"}</span>
                  <span className="sh-avatar sh-avatar-business">{initials(item.name)}</span>
                  <b className="text-capitalize">{item.name}</b>
                  <TypePill type="BUSINESS" />
                  <span className="text-muted small">{formatPct(item.shareholding_percentage)} of borrower</span>
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <div className="sh-progress">
                      <ProgressBar now={Math.min(ownersTotal, MAX_OWNER_DECLARATION)} variant={status.tone} />
                    </div>
                    <Pill tone={status.tone}>
                      {status.tone === "success" ? "✓" : "⚠"} {status.label}
                    </Pill>
                  </div>
                  {rowActions(item, item)}
                </div>
                {expanded && (
                  <div className="card-body">
                    {owners.length === 0 ? (
                      <p className="sh-empty-owners">
                        No individual shareholders added yet. Add the people who own {item.name} below.
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="sh-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th className="text-end">Share of {item.name}</th>
                              <th>Type</th>
                              <th>Beneficiary Owner</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {owners.map((owner) => (
                              <tr key={owner.uuid}>
                                <td>{nameCell(owner.name, false)}</td>
                                <td className="text-end sh-pct">{formatPct(owner.shareholding_percentage)}</td>
                                <td>
                                  <TypePill type={owner.shareholder_type} />
                                </td>
                                <td>
                                  <BeneficiaryPill isOwner={owner.is_business_owner} />
                                </td>
                                <td>{rowActions(owner, null)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* 3. Plain borrower-level rows: individuals, and companies under the
              UBO threshold that don't need their own owners declared. */}
          {plainRows.length > 0 && (
            <div className="sh-plain-card">
              <div className="table-responsive">
                <table className="sh-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="text-end">Shareholding</th>
                      <th>Type</th>
                      <th>Beneficiary Owner</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plainRows.map((item) => (
                      <tr key={item.uuid}>
                        <td>{nameCell(item.name, item.shareholder_type === "BUSINESS")}</td>
                        <td className="text-end sh-pct">{formatPct(item.shareholding_percentage)}</td>
                        <td>
                          <TypePill type={item.shareholder_type} />
                        </td>
                        <td>
                          <BeneficiaryPill isOwner={item.is_business_owner} />
                        </td>
                        <td>{rowActions(item, null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* 4. Total Shareholding, shown as progress against the same 50-100% rule */}
      <div className="sh-total-row">
        <div className="d-flex justify-content-between align-items-baseline mb-2">
          <b>Total Shareholding Added</b>
          <span className="sh-pct">{formatPct(totalShareholding)}</span>
        </div>
        <ProgressBar now={Math.min(totalShareholding, MAX_OWNER_DECLARATION)} variant={overallVariant(totalShareholding)} />
      </div>

      {/* 5. What still blocks this step, that isn't already shown on a card above
          (i.e. the borrower-company-level total, which isn't about any one row) */}
      {coverageErrors.length > 0 && (
        <Alert variant="warning" className="mb-0">
          <ul className="mb-0 ps-3">
            {coverageErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* 6. Confirmation Checkbox */}
      <Form.Check
        id="shareholding-pattern-acknowledgement"
        type="checkbox"
        label="I acknowledge that the above shareholding pattern is accurate and agree to proceed."
        checked={confirmed}
        onChange={(e) => setConfirmed(e.target.checked)}
      />

      {/* 7. Next Button */}
      <div className="d-flex justify-content-start">
        <Button
          id="b_next_shareholding_pattern"
          variant="primary"
          disabled={!canProceed}
          onClick={goToNextStep}
          style={{ minWidth: "240px" }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ShareholdersTable;
